import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ScreenshotBoard from "../../prototype/mobile-jigsaw/board/ScreenshotBoard";
import {
    BOARD_SIZE,
    PieceVisual,
    pieceVisualRenderSizePx,
} from "../../prototype/mobile-jigsaw/board/PieceVisual";
import { useBoardMatColor } from "../../prototype/mobile-jigsaw/board/useBoardMatColor";
import {
    getImageUrl,
    getTemplateGrid,
    loadScreenshotJigsaw,
} from "../../prototype/mobile-jigsaw/lib/api";
import { WEB_AD_JIGSAW_EXPECTED_GRID, WEB_AD_JIGSAW_STORY_ID } from "./webAdShots";

/**
 * Live jigsaw board + tray for web Google Ads.
 * Uses story {@link WEB_AD_JIGSAW_STORY_ID} (`/puzzle/play-jigsaw/:storyId`).
 */
export default function WebAdJigsawBoard({
    size = 340,
    showTray = true,
    trayBeside = false,
    exportLite = false,
}) {
    const { mat: boardMat, grooveStyle: boardGrooveStyle } = useBoardMatColor();
    const [puzzle, setPuzzle] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { group, template } = await loadScreenshotJigsaw({
                    storyId: WEB_AD_JIGSAW_STORY_ID,
                    expectedGrid: WEB_AD_JIGSAW_EXPECTED_GRID,
                });
                if (cancelled) return;
                const imageUrl = getImageUrl(group?.story);
                const grid = getTemplateGrid(template) || 0;
                const paths = template?.piecePaths?.pieces ?? template?.piecePaths;
                const total = grid * grid;
                const boardPieceIndices = Array.from({ length: total }, (_, i) => i).filter(
                    (i) => i % 5 !== 0 && i % 7 !== 0,
                );
                const onBoard = new Set(boardPieceIndices);
                const trayPieceIndices = Array.from({ length: total }, (_, i) => i).filter(
                    (i) => !onBoard.has(i),
                );
                setPuzzle({
                    imageUrl,
                    grid,
                    paths,
                    svgPathFallback: template?.svgPath ?? null,
                    boardPieceIndices,
                    trayPieceIndices,
                    ready: Boolean(imageUrl && grid > 0 && paths),
                });
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const boardScale = size / BOARD_SIZE;
    const cellPx = puzzle?.grid > 0 ? BOARD_SIZE / puzzle.grid : 0;
    const pieceScreenPx =
        cellPx > 0
            ? Math.max(22, Math.round(pieceVisualRenderSizePx(cellPx) * boardScale))
            : 40;
    const trayCirclePx = pieceScreenPx + 6;
    const trayPieces = useMemo(() => {
        if (!puzzle?.trayPieceIndices) return [];
        return puzzle.trayPieceIndices.slice(0, trayBeside ? 8 : 10);
    }, [puzzle, trayBeside]);

    if (failed) {
        return (
            <Box
                sx={{
                    width: size,
                    height: size,
                    borderRadius: 2,
                    bgcolor: "#E2E8F0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: "center",
                    px: 2,
                }}
            >
                Could not load jigsaw
            </Box>
        );
    }

    if (!puzzle?.ready) {
        return (
            <Box
                sx={{
                    width: size,
                    height: size,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress size={36} sx={{ color: "#3BA666" }} />
            </Box>
        );
    }

    const {
        imageUrl,
        grid,
        paths,
        svgPathFallback,
        boardPieceIndices,
    } = puzzle;

    const board = (
        <Box
            data-web-ad-jigsaw="1"
            sx={{
                width: size,
                height: size,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
                border: "3px solid #fff",
                flexShrink: 0,
                bgcolor: boardMat.hex,
            }}
        >
            <ScreenshotBoard
                grid={grid}
                imageUrl={imageUrl}
                paths={paths}
                svgPathFallback={svgPathFallback}
                boardPieceIndices={exportLite ? [] : boardPieceIndices}
                matPreset={boardMat}
                matColor={boardMat.hex}
                grooveStyle={boardGrooveStyle}
                showDieLines
                showFullImage={exportLite}
                fillHeight
                coverViewport={false}
                pieceBevel3d={false}
            />
        </Box>
    );

    const tray = showTray ? (
        <Box
            sx={{
                display: "flex",
                gap: 0.75,
                p: 1,
                bgcolor: "#1E293B",
                borderRadius: 2,
                boxShadow: "0 10px 28px rgba(15,23,42,0.2)",
                visibility: exportLite ? "hidden" : "visible",
                ...(trayBeside
                    ? {
                          flexDirection: "column",
                          width: trayCirclePx + 16,
                          alignItems: "center",
                          justifyContent: "center",
                      }
                    : {
                          width: size,
                          flexWrap: "wrap",
                          justifyContent: "center",
                          maxHeight: trayCirclePx * 2 + 20,
                          overflow: "hidden",
                      }),
            }}
        >
            {(exportLite ? [] : trayPieces).map((pieceIndex) => (
                <Box
                    key={pieceIndex}
                    sx={{
                        width: trayCirclePx,
                        height: trayCirclePx,
                        borderRadius: "50%",
                        overflow: "hidden",
                        bgcolor: "rgba(255,255,255,0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: "0 4px 12px rgba(15,23,42,0.14)",
                    }}
                >
                    <Box
                        sx={{
                            transform: `scale(${boardScale})`,
                            transformOrigin: "center center",
                            lineHeight: 0,
                        }}
                    >
                        <PieceVisual
                            pieceIndex={pieceIndex}
                            imageUrl={imageUrl}
                            grid={grid}
                            paths={paths}
                            showGridCoords={false}
                            anchorToCell={false}
                        />
                    </Box>
                </Box>
            ))}
        </Box>
    ) : null;

    if (trayBeside) {
        return (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "stretch" }}>
                {board}
                {tray}
            </Box>
        );
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "center" }}>
            {board}
            {tray}
        </Box>
    );
}

/** Convert remote SVG/img URLs inside a capture node so html-to-image can serialize them. */
export async function prepareWebAdCaptureNode(node) {
    if (!node) return;
    const svgImages = node.querySelectorAll("image");
    await Promise.all(
        [...svgImages].map(async (el) => {
            const href = el.getAttribute("href") || el.href?.baseVal || "";
            if (!href || href.startsWith("data:")) return;
            try {
                const dataUrl = await toEmbeddableDataUrl(href);
                el.setAttribute("href", dataUrl);
                if (el.href) el.href.baseVal = dataUrl;
            } catch {
                /* ignore */
            }
        }),
    );
    const htmlImages = node.querySelectorAll("img");
    await Promise.all(
        [...htmlImages].map(async (el) => {
            const href = el.currentSrc || el.src || "";
            if (!href || href.startsWith("data:") || href.startsWith("blob:")) return;
            if (
                typeof window !== "undefined"
                && (href.startsWith(window.location.origin) || href.startsWith("/"))
            ) {
                return;
            }
            try {
                el.src = await toEmbeddableDataUrl(href);
            } catch {
                /* ignore */
            }
        }),
    );
}

async function toEmbeddableDataUrl(url) {
    if (!url || typeof url !== "string") return url;
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    try {
        const res = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Failed to read image blob"));
            reader.readAsDataURL(blob);
        });
    } catch {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                try {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.naturalWidth || img.width;
                    canvas.height = img.naturalHeight || img.height;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        reject(new Error("Canvas unavailable"));
                        return;
                    }
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL("image/jpeg", 0.92));
                } catch (e) {
                    reject(e);
                }
            };
            img.onerror = () => reject(new Error(`Could not load image for export: ${url}`));
            const bust = `cb=${Date.now()}`;
            img.src = url.includes("?") ? `${url}&${bust}` : `${url}?${bust}`;
        });
    }
}
